import { useLoaderContext } from 'context/LoaderProvider';
import { setupContentUrl } from 'utils/contentUtils';
import { useEffect, useState, useMemo } from 'react';
import { fetchCountries, fetchCountryProviders, fetchProductsSummary } from 'services/Roaming.service';
import { fetchRoamingEuProducts, fetchRoamingImages } from 'services/Strapi.service';
import { isCanceledError } from 'utils/axiosUtils';
import { mapStrapiEuProductsResponse } from 'components/RoamingProductEU/RoamingProductEU.utils';
import {
  extractFlagsAndBannersFromStrapi,
  normalizeCountriesData,
  buildCountryAssets,
  sortCountries,
  extractCountryInfo,
  isSatelliteCountry,
} from 'pages/RoamingPage/RoamingPage.utils';
import useLocale from 'hooks/useLocale';

export const useRoamingPage = () => {
  const { setLoading } = useLoaderContext();
  const [unsortedCountries, setUnsortedCountries] = useState([]);
  const [countriesFlags, setCountriesFlags] = useState([]);
  const [countriesBanners, setCountriesBanners] = useState([]);
  const [roamingBannerData, setRoamingBannerData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryData, setCountryData] = useState(null);
  const [euProductInfo, setEuProductInfo] = useState(null);
  const [summaryProducts, setSummaryProducts] = useState(null);
  const [countryProducts, setCountryProducts] = useState(null);
  const productsInfo = countryProducts || summaryProducts;
  const locale = useLocale();

  const isEuZone = selectedCountry?.zoneCode === 1;
  const isSatelliteZone = selectedCountry ? isSatelliteCountry(selectedCountry) : false;

  const countries = useMemo(() => {
    return sortCountries(unsortedCountries, locale);
  }, [unsortedCountries, locale]);

  const { providersInfo, chargesInfo, networkInfo } = useMemo(() => {
    return extractCountryInfo(countryData, locale);
  }, [countryData, locale]);

  const preloadImage = (src) =>
    new Promise((resolve) => {
      if (!src) {
        resolve();
        return;
      }
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = src;
    });

  const onFormSubmit = (values) => {
    if (!values?.country || !values?.type?.value) {
      return Promise.resolve();
    }

    const isEu = values.country.zoneCode === 1;
    setLoading(true);

    const countryPromise = fetchCountryProviders(values.country.id, values.type.value);
    const euPromise = isEu ? fetchRoamingEuProducts(locale).catch(() => null) : Promise.resolve(null);

    const bannerEntry = countriesBanners.find((b) => b.countryId === values.country.id);
    const bannerPromise = preloadImage(bannerEntry?.src);

    return Promise.all([countryPromise, euPromise, bannerPromise])
      .then(([countryResponse, euResponse]) => {
        const euMapped = isEu && euResponse?.data ? mapStrapiEuProductsResponse(euResponse.data) : null;

        setSelectedCountry(values.country);
        setCountryData(countryResponse.data);
        setCountryProducts(countryResponse.data?.products || null);
        setEuProductInfo(euMapped);
      })
      .catch((error) => {
        if (!isCanceledError(error)) console.error(error);
        setCountryData(null);
        setCountryProducts(null);
        setEuProductInfo(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    const isEu = selectedCountry?.zoneCode === 1;

    const promises = [
      fetchCountries({ signal: controller.signal }),
      fetchRoamingImages(locale, { signal: controller.signal }).catch(() => null),
      isEu ? fetchRoamingEuProducts(locale,
          { signal: controller.signal })
          .catch(() => null) : Promise.resolve(null),
    ];

    Promise.all(promises)
      .then(([countriesResponse, contentResponse, euResponse]) => {
        const normalizedCountries = normalizeCountriesData(countriesResponse.data);

        let flagMap = new Map();
        let bannerMap = new Map();
        let satelliteFlag = null;
        let satelliteBanner = null;
        let roamingBlock = null;

        if (contentResponse?.data) {
          const extracted = extractFlagsAndBannersFromStrapi(contentResponse.data);
          flagMap = extracted.flagMap;
          bannerMap = extracted.bannerMap;
          satelliteFlag = extracted.satelliteFlag;
          satelliteBanner = extracted.satelliteBanner;
          roamingBlock = extracted.roamingBlock;
        }

        const flags = buildCountryAssets(normalizedCountries, flagMap, satelliteFlag);
        const banners = buildCountryAssets(normalizedCountries, bannerMap, satelliteBanner);

        const roamingBannerSrc = roamingBlock?.imageUrl ? setupContentUrl(roamingBlock.imageUrl) : null;

        return preloadImage(roamingBannerSrc).then(() => {
          setUnsortedCountries(normalizedCountries);
          setCountriesFlags(flags);
          setCountriesBanners(banners);
          setRoamingBannerData(roamingBlock);

          if (isEu && euResponse?.data) {
            setEuProductInfo(mapStrapiEuProductsResponse(euResponse.data));
          }
        });
      })
      .catch((error) => {
        if (!isCanceledError(error)) {
          console.error(error);
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => controller.abort();
  }, [locale]);

  useEffect(() => {
    const controller = new AbortController();

    fetchProductsSummary({ signal: controller.signal })
      .then((response) => {
        if (response?.data) {
          setSummaryProducts(response.data);
        }
      })
      .catch((error) => {
        if (!isCanceledError(error)) {
          console.error(error);
        }
      });

    return () => controller.abort();
  }, []);

  return {
    countries,
    countriesFlags,
    countriesBanners,
    roamingBannerData,
    selectedCountry,
    providersInfo,
    chargesInfo,
    networkInfo,
    isEuZone,
    productsInfo,
    euProductInfo,
    isSatelliteZone,
    onFormSubmit,
  };
};
